import { orderBy } from 'lodash-es'
import { DateTime } from 'luxon'

import SeriesQ from '@/queries/SeriesQ'
import SeriesReservationQ from '@/queries/SeriesReservationQ'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { useServerNow } from '@/client/hooks'
import { SQL } from '@/client/query'
import * as url from '@/client/services/url'
import { getDashboardProgramsUrl } from '@/client/services/url'
import { SAT_BOOTCAMP_WORLD_SLUG } from '@/client/services/worlds/constants'

import useSATBootcampLearnerSurveyTodos from '@/components/SATBootcamp/hooks/todos/useSATBootcampLearnerSurveyTodos'
import { Priority, type TodoItem } from '@/components/Worlds/Todos/types'

export default function useSATBootcampLearnerTodos(): TodoItem[] {
  const user = useUser()
  const query = useQuery()
  const now = useServerNow()
  const surveyTodos = useSATBootcampLearnerSurveyTodos()

  const me = query.one(UserQ.where({ id: user.id }))
  const registeredForBootcamp = me(UserQ.isRegisteredForSATBootcamp(UserQ.id)) ?? false

  const learnerConsentCompletedAt = me(UserQ.parent_consent_completed_at) ?? null
  const learnerConsentWaivedAt = me(UserQ.parent_consent_waived_at) ?? null

  const bootcampSelfReservation =
    query.one(
      SeriesReservationQ.where(
        {
          limit: 1,
          userId: user.id,
          reserverUserId: user.id,
          order: [[SeriesReservationQ.expired_at, 'asc']],
          include: [
            {
              association: 'Series',
              required: true,
              where: SeriesQ.isSATSeries(),
            },
          ],
        },
        SQL`${SeriesReservationQ.expired_at} > NOW()`
      )
    ) ?? false

  const bootcampSelfReservationExpirationDate = bootcampSelfReservation(
    SeriesReservationQ.expired_at
  )

  const registerForBootcamp: TodoItem = {
    worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
    title: 'Register for an SAT Bootcamp',
    subtitle: 'To feel prepared to tackle the test!',
    completed: registeredForBootcamp || bootcampSelfReservationExpirationDate != null,
    link: getDashboardProgramsUrl({ programSlug: SAT_BOOTCAMP_WORLD_SLUG }),
    priority: Priority.High,
    emoji: '📖',
    deadline: null,
    targetLevel: 'member',
  }
  const reservationTodos: TodoItem[] =
    bootcampSelfReservationExpirationDate == null
      ? []
      : [
          {
            worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
            title: 'Complete parent consent',
            subtitle: 'To confirm your registration',
            completed: learnerConsentCompletedAt != null || learnerConsentWaivedAt != null,
            link: url.getRequestParentConsentUrl(),
            priority: Priority.High,
            emoji: '📝',
            deadline: DateTime.fromISO(bootcampSelfReservationExpirationDate),
            targetLevel: 'member',
            showOnSeriesPage: true,
          },
        ]

  const allTodos = [registerForBootcamp, ...reservationTodos, ...surveyTodos]

  return orderBy(
    allTodos.filter(todo => todo.deadline == null || now < todo.deadline),
    todo => todo.deadline?.toMillis() ?? Number.POSITIVE_INFINITY
  )
}
